import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from '../../auth/AuthContext';
import PageTemplate from '../../components/PageTemplate/PageTemplate';
import Error404Screen from '../ErrorScreen/Error404Screen';
import { useFormConfig } from '../../hooks/useFormConfig';
import { useFormData } from '../../hooks/useFormData';
import { SUPPRESS_GLOBAL_LOADING_META } from '../../context/UiContext';
import type { RewardCosAuditEntry } from '../../service/types';
import { formatLondonDateTime, londonNow } from '../../utils/londonTime';
import { ApiError } from '../../utils/api-error';
import ModerationDialog from './ModerationDialog';
import ModerationReportDownload from './ModerationReportDownload';
import ModerationDistribution, { MARKINGS, POSITIONS } from './ModerationDistribution';
import { buildModerationPreview } from './preview';
import {
  fetchModerationAudit,
  fetchModerationForms,
  fetchModerationPreview,
  saveModeration,
  type ModerationPeriod,
  type ModerationRow,
} from './service';
import './ScsModeration.scss';

const QUERY_KEY = ['scs-moderation'];
const EMPTY_ROWS: ModerationRow[] = [];
const errorMessage = (error: unknown) =>
  error instanceof ApiError
    ? error.detail || error.message
    : error instanceof Error
      ? error.message
      : 'Unable to load moderation.';
const currentYear = () => {
  const now = londonNow();
  const year = now.getFullYear() - (now.getMonth() < 3 ? 1 : 0);
  return `${year}-${String(year + 1).slice(-2)}`;
};

type Popup = {
  title: string;
  rows?: ModerationRow[];
  html?: string[];
  audit?: RewardCosAuditEntry[];
  formId?: string;
  error?: string;
};

export default function ScsModeration() {
  const { tenant, formConfig } = useFormConfig();
  const { data: session, isLoading: sessionLoading, error: sessionError } = useFormData();
  const { fetchJWT } = useAuthContext();
  const queryClient = useQueryClient();
  const [year, setYear] = useState(currentYear);
  const [grade, setGrade] = useState('');
  const [period, setPeriod] = useState<ModerationPeriod>('Q2');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [revert, setRevert] = useState<ModerationRow | null>(null);
  const [popup, setPopup] = useState<Popup | null>(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const authorised = tenant === 'scs' && session?.hasModerationAccess === true;
  const token = async () => {
    const value = (await fetchJWT()).idToken;
    if (!value) throw new Error('Not authenticated');
    return value;
  };
  const list = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => fetchModerationForms(await token()),
    enabled: authorised,
    retry: false,
    meta: SUPPRESS_GLOBAL_LOADING_META,
  });
  const rows = list.data || EMPTY_ROWS;
  const years = Array.from(new Set([currentYear(), ...rows.map((row) => row.reportYear)]))
    .filter(Boolean)
    .sort()
    .reverse();
  const grades = Array.from(
    new Set([
      'SCS 1',
      'SCS 2',
      'SCS 3',
      'SCS 4',
      ...rows.filter((row) => row.reportYear === year).map((row) => row.grade),
    ]),
  )
    .filter(Boolean)
    .sort();
  const cohort = rows.filter(
    (row) => row.period === period && row.reportYear === year && (!grade || row.grade === grade),
  );
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows
      .filter(
        (row) =>
          row.period === period &&
          row.reportYear === year &&
          (!grade || row.grade === grade) &&
          (!term ||
            row.scsName.toLowerCase().includes(term) ||
            row.lineManager.toLowerCase().includes(term)),
      )
      .sort((a, b) => a.scsName.localeCompare(b.scsName));
  }, [rows, period, year, grade, search]);
  useEffect(() => {
    setSelected((previous) => previous.filter((id) => filtered.some((row) => row.formId === id)));
  }, [filtered]);
  const complete = cohort.filter((row) => row.moderationComplete).length;
  const mutation = useMutation({
    mutationFn: async ({ row, complete }: { row: ModerationRow; complete: boolean }) =>
      saveModeration(await token(), row, complete),
    onSuccess: (updated) => {
      queryClient.setQueryData<ModerationRow[]>(QUERY_KEY, (previous) =>
        previous?.map((row) =>
          row.formId === updated.formId && row.period === updated.period ? updated : row,
        ),
      );
      setRevert(null);
    },
  });
  const select = (formId: string) =>
    setSelected((previous) =>
      previous.includes(formId)
        ? previous.filter((id) => id !== formId)
        : [...previous, formId].slice(-2),
    );
  const openPreview = async (row?: ModerationRow) => {
    const targets =
      selected.length === 2
        ? selected.map((id) => filtered.find((item) => item.formId === id)!).filter(Boolean)
        : row
          ? [row]
          : [];
    if (!targets.length) return;
    const state: Popup = {
      title: targets.length === 2 ? 'SCS form comparison' : 'SCS form',
      rows: targets,
    };
    setPopup(state);
    try {
      const jwt = await token();
      const html = await Promise.all(
        targets.map(async (target) =>
          buildModerationPreview(await fetchModerationPreview(jwt, target)),
        ),
      );
      setPopup((current) => (current === state ? { ...state, html } : current));
    } catch (error) {
      setPopup((current) =>
        current === state ? { ...state, error: errorMessage(error) } : current,
      );
    }
  };
  const openAudit = async (row: ModerationRow) => {
    const state: Popup = { title: `Audit - ${row.scsName}`, formId: row.formId };
    setPopup(state);
    try {
      const result = await fetchModerationAudit(await token(), row);
      setPopup((current) =>
        current === state ? { ...state, audit: result.audit || [] } : current,
      );
    } catch (error) {
      setPopup((current) =>
        current === state ? { ...state, error: errorMessage(error) } : current,
      );
    }
  };

  if (tenant !== 'scs') return <Error404Screen />;
  return (
    <PageTemplate title="Moderation" fullWidth forceLoadingOverlay={generatingReport}>
      {sessionLoading ? (
        <p className="govuk-body">Loading moderation access...</p>
      ) : sessionError ? (
        <p role="alert" className="govuk-error-message">
          {errorMessage(sessionError)}
        </p>
      ) : !authorised ? (
        <p role="alert" className="govuk-body">
          You do not have access to moderation.
        </p>
      ) : (
        <div className="scs-moderation" {...(generatingReport ? { inert: '' } : {})}>
          <ModerationReportDownload
            rows={rows}
            period={period}
            year={year}
            config={formConfig}
            token={token}
            disabled={!list.isSuccess || list.isFetching || generatingReport}
            onGenerating={setGeneratingReport}
          />
          <div className="scs-moderation__filters">
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="moderation-year">
                Reporting Year
              </label>
              <select
                className="govuk-select"
                id="moderation-year"
                value={year}
                onChange={(event) => {
                  setYear(event.target.value);
                  setGrade('');
                  setSelected([]);
                }}
              >
                {years.map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </div>
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="moderation-grade">
                Grade
              </label>
              <select
                className="govuk-select"
                id="moderation-grade"
                value={grade}
                onChange={(event) => {
                  setGrade(event.target.value);
                  setSelected([]);
                }}
              >
                <option value="">All grades</option>
                {grades.map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </div>
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="moderation-period">
                Moderation period
              </label>
              <select
                className="govuk-select"
                id="moderation-period"
                value={period}
                onChange={(event) => {
                  setPeriod(event.target.value as ModerationPeriod);
                  setSelected([]);
                }}
              >
                <option value="Q2">Mid-year</option>
                <option value="Q4">End of Year</option>
              </select>
            </div>
          </div>
          {list.isPending ? (
            <p className="govuk-body" role="status">
              Loading moderation records...
            </p>
          ) : list.isError ? (
            <p role="alert" className="govuk-error-message">
              {errorMessage(list.error)}
            </p>
          ) : (
            <>
              <div className="scs-moderation__kpis" aria-live="polite">
                <div>
                  <h2 className="govuk-heading-s">Awaiting Moderation</h2>
                  <strong>{cohort.length - complete}</strong>
                </div>
                <div>
                  <h2 className="govuk-heading-s">Complete</h2>
                  <strong>{complete}</strong>
                </div>
              </div>
              <ModerationDistribution
                title="Talent Marking"
                values={cohort.map((row) => row.indicativeMarking)}
                outcomes={MARKINGS}
              />
              <ModerationDistribution
                title="Talent Position"
                values={cohort.map((row) => row.indicativePosition)}
                outcomes={POSITIONS}
              />
              <div className="govuk-form-group">
                <label className="govuk-label" htmlFor="moderation-search">
                  Search SCS name or line manager
                </label>
                <input
                  className="govuk-input"
                  id="moderation-search"
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
              <div className="scs-moderation__toolbar">
                <button
                  type="button"
                  className="govuk-button govuk-button--secondary"
                  disabled={selected.length !== 2}
                  onClick={() => void openPreview()}
                >
                  Compare selected staff
                </button>
                <p className="govuk-hint" aria-live="polite">
                  {selected.length} selected. Only the last two selections are kept.
                </p>
              </div>
              {mutation.isError && !revert && (
                <p className="govuk-error-message" role="alert">
                  {errorMessage(mutation.error)}
                </p>
              )}
              <div className="scs-moderation__table-scroll">
                <table className="govuk-table">
                  <caption className="govuk-visually-hidden">SCS moderation records</caption>
                  <thead className="govuk-table__head">
                    <tr className="govuk-table__row">
                      <th scope="col" className="govuk-table__header">
                        <span className="govuk-visually-hidden">Select</span>
                      </th>
                      {[
                        'SCS Name',
                        'Line Manager',
                        'Indicative Marking',
                        'Indicative Position',
                      ].map((title) => (
                        <th scope="col" className="govuk-table__header" key={title}>
                          {title}
                        </th>
                      ))}
                      <th scope="col" className="govuk-table__header">
                        Manager
                        <br />
                        assessment
                        <br />
                        complete
                      </th>
                      <th scope="col" className="govuk-table__header">
                        Moderation
                        <br />
                        Complete
                      </th>
                      <th scope="col" className="govuk-table__header">
                        <span className="govuk-visually-hidden">View form</span>
                      </th>
                      <th scope="col" className="govuk-table__header">
                        <span className="govuk-visually-hidden">Audit</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="govuk-table__body">
                    {filtered.map((row) => (
                      <tr className="govuk-table__row" key={row.formId}>
                        <td className="govuk-table__cell">
                          <input
                            type="checkbox"
                            checked={selected.includes(row.formId)}
                            onChange={() => select(row.formId)}
                            aria-label={`Select ${row.scsName}`}
                          />
                        </td>
                        <th scope="row" className="govuk-table__header">
                          {row.scsName}
                        </th>
                        <td className="govuk-table__cell">{row.lineManager}</td>
                        <td className="govuk-table__cell">{row.indicativeMarking}</td>
                        <td className="govuk-table__cell">{row.indicativePosition}</td>
                        <td className="govuk-table__cell">
                          <span
                            className={`scs-moderation__status ${row.managerAssessmentComplete ? 'is-complete' : ''}`}
                            role="img"
                            aria-label={
                              row.managerAssessmentComplete
                                ? 'Manager assessment complete'
                                : 'Manager assessment incomplete'
                            }
                          >
                            {row.managerAssessmentComplete ? '✓' : '✗'}
                          </span>
                        </td>
                        <td className="govuk-table__cell">
                          <button
                            type="button"
                            className={`scs-moderation__status-button ${row.moderationComplete ? 'is-complete' : ''}`}
                            aria-label={`Mark ${row.scsName} moderation ${row.moderationComplete ? 'incomplete' : 'complete'}`}
                            aria-pressed={row.moderationComplete}
                            disabled={mutation.isPending}
                            onClick={() => {
                              mutation.reset();
                              row.moderationComplete
                                ? setRevert(row)
                                : mutation.mutate({ row, complete: true });
                            }}
                          >
                            <span aria-hidden="true">{row.moderationComplete ? '✓' : '✗'}</span>
                          </button>
                        </td>
                        <td className="govuk-table__cell">
                          <button
                            type="button"
                            className="scs-moderation__icon govuk-button govuk-button--secondary"
                            aria-label={`View ${row.scsName}`}
                            title="View form"
                            onClick={() => void openPreview(row)}
                          >
                            <svg
                              aria-hidden="true"
                              viewBox="0 0 512 512"
                              width="22"
                              height="22"
                              fill="currentColor"
                              focusable="false"
                            >
                              <path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376C296.3 401.1 253.9 416 208 416C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z" />
                            </svg>
                          </button>
                        </td>
                        <td className="govuk-table__cell">
                          <button
                            type="button"
                            className="scs-moderation__icon govuk-button govuk-button--secondary"
                            aria-label={`Audit ${row.scsName}`}
                            title="Show audit"
                            onClick={() => void openAudit(row)}
                          >
                            <svg
                              aria-hidden="true"
                              viewBox="0 0 24 24"
                              width="20"
                              height="20"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M4 6h16M4 12h16M4 18h10M18 16v4M16 18h4" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!filtered.length && (
                      <tr>
                        <td colSpan={9} className="govuk-table__cell">
                          No completed staff assessments match these filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <button
            type="button"
            className="govuk-button govuk-button--secondary"
            disabled={list.isFetching || mutation.isPending}
            onClick={() => {
              mutation.reset();
              void list.refetch();
            }}
          >
            Refresh moderation
          </button>
        </div>
      )}
      {revert && (
        <ModerationDialog
          title="Revert moderation"
          onClose={() => {
            if (!mutation.isPending) setRevert(null);
          }}
        >
          <div className="scs-moderation-dialog__content">
            <p className="govuk-body">
              Are you sure you want to revert {revert.scsName} as Moderation not completed?
            </p>
            {mutation.isError && (
              <p className="govuk-error-message" role="alert">
                {errorMessage(mutation.error)}
              </p>
            )}
            <button
              type="button"
              className="govuk-button govuk-button--warning"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate({ row: revert, complete: false })}
            >
              Revert moderation
            </button>{' '}
            <button
              type="button"
              className="govuk-button govuk-button--secondary"
              disabled={mutation.isPending}
              onClick={() => {
                mutation.reset();
                setRevert(null);
              }}
            >
              Cancel
            </button>
          </div>
        </ModerationDialog>
      )}
      {popup && (
        <ModerationDialog
          title={popup.title}
          comparison={popup.rows?.length === 2}
          onClose={() => setPopup(null)}
        >
          <div className="scs-moderation-dialog__content">
            {popup.error ? (
              <p className="govuk-error-message" role="alert">
                {popup.error}
              </p>
            ) : popup.html ? (
              <div
                className={`scs-moderation-dialog__panes${popup.rows?.length === 2 ? ' scs-moderation-dialog__panes--comparison' : ''}`}
              >
                {popup.rows?.map((row, index) => (
                  <section key={row.formId}>
                    <h3 className="govuk-heading-s">{row.scsName}</h3>
                    <iframe
                      sandbox=""
                      title={`SCS form - ${row.scsName}`}
                      srcDoc={popup.html?.[index]}
                    />
                  </section>
                ))}
              </div>
            ) : popup.audit ? (
              <>
                <p className="govuk-hint">Form ID: {popup.formId}</p>
                <table className="govuk-table">
                  <thead>
                    <tr>
                      {['Date', 'Action', 'Completed by', 'Sent to'].map((title) => (
                        <th scope="col" className="govuk-table__header" key={title}>
                          {title}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {popup.audit.map((entry, index) => (
                      <tr key={`${entry.actionDate}-${index}`}>
                        <td className="govuk-table__cell">
                          {entry.actionDate ? formatLondonDateTime(entry.actionDate) : ''}
                        </td>
                        <td className="govuk-table__cell">{entry.action}</td>
                        <td className="govuk-table__cell">{entry.completedBy}</td>
                        <td className="govuk-table__cell">{entry.sentTo}</td>
                      </tr>
                    ))}
                    {!popup.audit.length && (
                      <tr>
                        <td colSpan={4} className="govuk-table__cell">
                          No audit records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </>
            ) : (
              <p className="govuk-body" role="status">
                Loading...
              </p>
            )}
          </div>
        </ModerationDialog>
      )}
    </PageTemplate>
  );
}
